/* eslint-disable react/prop-types */
import { Minus, Plus } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setSplitCount, updatePaymentAmount, updatePaymentMethod, updatePaymentName, updatePaymentTip, removePayment, addPaymentSlot, newSplit, setPaymentAmount, setPayments, setSplitData, resetSplit } from "../../redux/slice/splitPaymentSlice";
import ErrorBoundary from "../common/ErrorBoundary";
import { Skeleton } from "../ui/skeleton";
import { FaXmark } from "react-icons/fa6";
import toast from "react-hot-toast";
import { DefaultDialog } from "../dialog/default-dialog";
import { getPaymentSplitApi, updatePaymentSplitApi } from "@/api/take-order/take-order";
import { printSplitPaymentCopy } from "@/lib/print-slip";
import useAuth from "@/hooks/useAuth";
import useSettings from "@/hooks/useSettings";
import { setOrderData } from "@/redux/slice/take-order-slice/order-slice";
import PusherHandler from "@/api/lib/pusher/PusherHandler";
import TipsCourseWiseAndSplit from "./tipsCourseWiseAndSplit";
import SplitCardPaymentConfirmDialog from "./SplitCardPaymentConfirmDialog";
import OrderCardPaymentResponseDialog from "./OrderCardPaymentResponseDialog";
import PaxPaymentTimeoutDialog from "./PaxPaymentTimeoutDialog";
import getOrderAdvanceDetails from "@/lib/orderAdvanceUtils";

function SplitPayment({ setSplitPaymentPage, onConfirm }) {
    const dispatch = useDispatch();
    const { id: splitPaymentId, order_id: splitOrderId, splitCount, totalAmount, receivedAmount, remainingAmount, payments } = useSelector((state) => state.splitPayment);
    const { orderData } = useSelector((state) => state.takeOrder.order);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cashReceived, setCashReceived] = useState(0);
    const [cashAmount, setCashAmount] = useState(0);
    const [activePaymentId, setActivePaymentId] = useState(null);
    const [cardConfirmPayment, setCardConfirmPayment] = useState(null);
    const [cardChargeAmount, setCardChargeAmount] = useState(0);
    const [isCardLoading, setIsCardLoading] = useState(false);
    const [cardResponseData, setCardResponseData] = useState(null);
    const [isCardResponseOpen, setIsCardResponseOpen] = useState(false);
    const [isPaxTimeoutDialogOpen, setIsPaxTimeoutDialogOpen] = useState(false);
    const [paxTimeoutData, setPaxTimeoutData] = useState(null);
    const [tipsFor, setTipsFor] = useState(null);
    const [tipType, setTipType] = useState("fixed");
    const [tipAmount, setTipAmount] = useState("");
    const { user } = useAuth();
    const { settings } = useSettings();
    const restaurant_id = user?.restaurant_id;
    const [isCompleted, setIsCompleted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { addEventHandler, removeEventHandler } = PusherHandler();

    useEffect(() => {
        const advanceInfo = getOrderAdvanceDetails(orderData);
        const hasAdvanceDeposit = Boolean(
            (advanceInfo?.isPaid && advanceInfo?.advanceAmount > 0) ||
            (Number(orderData?.advance || 0) > 0 && orderData?.advance_payment?.status === "paid") ||
            (Boolean(orderData?.reservation?.is_deposit_paid) && Number(orderData?.reservation?.advance || 0) > 0)
        );
        if (hasAdvanceDeposit) {
            toast.error("Split payment is not available for orders with an advance deposit.");
            setSplitPaymentPage(false);
            return;
        }

        setIsCompleted(false);
        if (orderData?.id) {
            dispatch(resetSplit({
                totalAmount: orderData.total_price || 0,
                order_id: orderData.id,
            }));
        }
        return () => {
            dispatch(resetSplit({ totalAmount: 0, order_id: null }));
        };
    }, [orderData, dispatch, setSplitPaymentPage]);

    const saveTipsInfo = (rawValue) => {
        const payment = payments.find((p) => p.front_id === tipsFor);
        if (!payment) return;
        const parsed = parseFloat(rawValue);
        const value = Number.isFinite(parsed) ? parsed : 0;
        const baseAmount = Number(payment.amount) || 0;
        const computedTipAmount = tipType === "percentage"
            ? Math.round((baseAmount * value / 100) * 100) / 100
            : Math.round(value * 100) / 100;

        dispatch(updatePaymentTip({
            front_id: payment.front_id,
            tipType,
            tipValue: value,
            tipAmount: computedTipAmount,
        }));
    };

    const handleSplitChange = (increment) => {
        if(isCompleted){
            toast.error("Payment is already completed. No further changes allowed.");
            return;
        }

        if(receivedAmount == 0){
            let newSplitCount = splitCount + increment;
            if (newSplitCount < 2) newSplitCount = 2;
            dispatch(setSplitCount(newSplitCount));
        }
        else{
            if (increment > 0) {
                dispatch(newSplit()); // Add a new split with the remaining value
            } else if (splitCount > 1) {
                dispatch(removePayment({ front_id: payments[payments.length - 1].front_id })); // Remove the last split
            }
        }
    };

    const handleAdjustAmount = (index, delta) => {
        if (isCompleted) {
            toast.error("Payment is already completed. No further changes allowed.");
            return;
        }
        const currentPayment = payments[index];
        if (!currentPayment) return;
        const currentVal = Number(currentPayment.amount) || 0;
        const newVal = Math.max(0, Math.round((currentVal + delta) * 100) / 100);
        const totalSlotAmount = currentPayment.slots.reduce((slotSum, slot) => slotSum + slot.amount, 0);

        if (newVal < totalSlotAmount) {
            toast.error("Cannot reduce below the total slot amount.");
            return;
        }

        dispatch(updatePaymentAmount({ index, value: newVal }));
    };


    // const confirmSplit = () => {
    //     console.log("Render SplitPayment", {splitCount, totalAmount, receivedAmount, remainingAmount, payments});

    //     // if (remainingAmount > 0) {
    //     //     toast.error("Please complete all payments before confirming.");
    //     //     return;
    //     // }

    //     const dataToExport = {
    //         totalAmount,
    //         receivedAmount,
    //         remainingAmount,
    //         splitCount,
    //         payments: payments
    //     };

    //     const jsonString = JSON.stringify(dataToExport, null, 2);
    //     const blob = new Blob([jsonString], { type: "application/json" });
    //     const url = URL.createObjectURL(blob);
    //     const link = document.createElement("a");
    //     link.href = url;
    //     link.download = `split-payment-${new Date().toISOString().split('T')[0]}.json`;
    //     link.click();
    //     URL.revokeObjectURL(url);

    //     // onConfirm();
    // };

    // console.log("orderData", orderData);





    const handleOpenCardModal = (payment) => {
        if (isCompleted) {
            toast.error("Payment is already completed. No further changes allowed.");
            return;
        }

        const totalSlotAmount = payment.slots.reduce((sum, slot) => sum + slot.amount, 0);
        const paymentDue = (Number(payment.amount) || 0) + (Number(payment.tipAmount) || 0);
        const remainingDue = Math.max(0, Math.round((paymentDue - totalSlotAmount) * 100) / 100);

        if (remainingDue <= 0) {
            toast.error("This split payment is already fully paid.");
            return;
        }

        setCardConfirmPayment(payment);
        setCardChargeAmount(remainingDue);
    };

    const handleDispatchCardPayment = async (selectedAmount) => {
        if (!cardConfirmPayment) return;
        if (!restaurant_id || !orderData?.id) {
            toast.error("Restaurant or Order ID missing.");
            return;
        }

        const amountToPay = selectedAmount || cardChargeAmount;
        if (!amountToPay || amountToPay <= 0) {
            toast.error("Please enter a valid amount.");
            return;
        }

        setIsCardLoading(true);
        try {
            const updatedPayments = payments.map((payment, idx) => {
                const isCurrentPayment = (cardConfirmPayment.front_id && payment.front_id === cardConfirmPayment.front_id) || 
                    (cardConfirmPayment.id && payment.id === cardConfirmPayment.id) ||
                    (payment.name === cardConfirmPayment.name) ||
                    (idx === 0 && !cardConfirmPayment.front_id && !cardConfirmPayment.id);

                const existingSlots = Array.isArray(payment.slots) ? payment.slots.map(s => ({
                    ...(s.id ? { id: s.id } : {}),
                    ...(s.order_payment_split_payment_id ? { order_payment_split_payment_id: s.order_payment_split_payment_id } : {}),
                    method: (s.method || "card").toLowerCase(),
                    ...(s.type ? { type: s.type } : {}),
                    ...(s.terminal_payment_id ? { terminal_payment_id: s.terminal_payment_id } : {}),
                    amount: Number(s.amount),
                    received: Number(s.received ?? s.amount ?? 0),
                    change: Number(s.change ?? 0),
                    ...(s.reference ? { reference: s.reference } : {}),
                })) : [];

                return {
                    ...(payment.id ? { id: payment.id } : {}),
                    ...(payment.restaurant_id ? { restaurant_id: payment.restaurant_id } : {}),
                    ...(payment.order_id ? { order_id: payment.order_id } : {}),
                    front_id: payment.front_id,
                    name: payment.name || `Payment ${idx + 1}`,
                    amount: Number(payment.amount),
                    confirm: payment.confirm || false,
                    receivedAmount: Number(payment.receivedAmount ?? payment.received_amount ?? 0),
                    tipType: payment.tipType || payment.tip_type || "fixed",
                    tipValue: Number(payment.tipValue || payment.tip_value || 0),
                    tipAmount: Number(payment.tipAmount || payment.tip_amount || 0),
                    ...(payment.pay_amount !== undefined && payment.pay_amount !== null ? { pay_amount: payment.pay_amount } : {}),
                    ...(payment.change_amount !== undefined && payment.change_amount !== null ? { change_amount: payment.change_amount } : {}),
                    ...(payment.advance_deducted !== undefined && payment.advance_deducted !== null ? { advance_deducted: payment.advance_deducted } : {}),
                    ...(payment.payment_method ? { payment_method: payment.payment_method } : {}),
                    ...(payment.payment_type ? { payment_type: payment.payment_type } : {}),
                    ...(payment.status ? { status: payment.status } : {}),
                    slots: isCurrentPayment ? [
                        ...existingSlots,
                        {
                            method: "card",
                            amount: Number(amountToPay),
                        }
                    ] : existingSlots,
                };
            });

            const newReceivedAmount = updatedPayments.reduce((total, p) => 
                total + p.slots.reduce((slotSum, s) => slotSum + Number(s.amount), 0), 0
            );
            const newRemainingAmount = Math.max(0, Math.round((totalAmount - newReceivedAmount) * 100) / 100);

            const isValidSplitId = splitPaymentId && Number(splitOrderId) === Number(orderData?.id);
            const terminalPayload = {
                ...(isValidSplitId ? { id: splitPaymentId } : {}),
                totalAmount: Number(totalAmount),
                receivedAmount: Math.round(newReceivedAmount * 100) / 100,
                remainingAmount: newRemainingAmount,
                splitCount: Number(splitCount),
                till_id: Number(settings?.take_order?.device_id ?? settings?.device_id ?? 1),
                splitPayments: updatedPayments,
            };

            console.log("Dispatching Split Card Payment via POST split-payments with payload:", terminalPayload);

            const response = await updatePaymentSplitApi(
                restaurant_id,
                orderData.id,
                terminalPayload
            );

            console.log("Split Card Payment Response --->", response);

            if (response?.status === "success") {
                toast.success(response?.message || "Payment dispatched to the terminal.");

                // Open response dialog (do NOT add payment slot until confirmed by Pusher event)
                setCardResponseData(response);
                setIsCardResponseOpen(true);
                setCardConfirmPayment(null);

                // If backend returns updated order data, update it
                if (response?.data?.order) {
                    dispatch(setOrderData(response.data.order));
                }

                // If backend indicates completed
                if (response?.data?.is_payment_completed) {
                    setIsCompleted(true);
                }
            } else {
                toast.error(response?.message || "Failed to process card payment.");
            }
        } catch (error) {
            console.error("Error in split card payment:", error);
            toast.error("Error processing card payment. Please try again.");
        } finally {
            setIsCardLoading(false);
        }
    };

    function updatePaymentSplit(){
        if(isCompleted){
            toast.error("Payment is already completed. No further changes allowed.");
            return;
        }
        const updatedPayments = payments.map((payment, idx) => ({
            ...(payment.id ? { id: payment.id } : {}),
            ...(payment.restaurant_id ? { restaurant_id: payment.restaurant_id } : {}),
            ...(payment.order_id ? { order_id: payment.order_id } : {}),
            front_id: payment.front_id,
            name: payment.name || `Payment ${idx + 1}`,
            amount: Number(payment.amount),
            confirm: payment.confirm || false,
            receivedAmount: Number(payment.receivedAmount ?? payment.received_amount ?? 0),
            tipType: payment.tipType || payment.tip_type || "fixed",
            tipValue: Number(payment.tipValue || payment.tip_value || 0),
            tipAmount: Number(payment.tipAmount || payment.tip_amount || 0),
            ...(payment.pay_amount !== undefined && payment.pay_amount !== null ? { pay_amount: payment.pay_amount } : {}),
            ...(payment.change_amount !== undefined && payment.change_amount !== null ? { change_amount: payment.change_amount } : {}),
            ...(payment.advance_deducted !== undefined && payment.advance_deducted !== null ? { advance_deducted: payment.advance_deducted } : {}),
            ...(payment.payment_method ? { payment_method: payment.payment_method } : {}),
            ...(payment.payment_type ? { payment_type: payment.payment_type } : {}),
            ...(payment.status ? { status: payment.status } : {}),
            slots: Array.isArray(payment.slots) ? payment.slots.map(s => ({
                ...(s.id ? { id: s.id } : {}),
                ...(s.order_payment_split_payment_id ? { order_payment_split_payment_id: s.order_payment_split_payment_id } : {}),
                method: (s.method || "card").toLowerCase(),
                ...(s.type ? { type: s.type } : {}),
                ...(s.terminal_payment_id ? { terminal_payment_id: s.terminal_payment_id } : {}),
                amount: Number(s.amount),
                received: Number(s.received ?? s.amount ?? 0),
                change: Number(s.change ?? 0),
                ...(s.reference ? { reference: s.reference } : {}),
            })) : [],
        }));

        const isValidSplitId = splitPaymentId && Number(splitOrderId) === Number(orderData?.id);
        updatePaymentSplitApi(restaurant_id, orderData.id, {
            ...(isValidSplitId ? { id: splitPaymentId } : {}),
            order_id: orderData.id,
            totalAmount,
            receivedAmount,
            remainingAmount,
            splitCount,
            till_id: Number(settings?.take_order?.device_id ?? settings?.device_id ?? 1),
            splitPayments: updatedPayments
        })
        .then((response) => {
            console.log(" payment split response->", response);
            console.log(response?.data);
            console.log(response?.data?.is_payment_completed);
            if (response.status === "success") {
                console.log("Payment split updated successfully:", response);
                
                if(response?.data?.is_payment_completed && response?.data?.is_payment_completed == true){
                    toast.success("Payment completed successfully");
                    setSplitPaymentPage(false)
                }else{
                    toast.success("Payment split saved successfully");
                }
                

                dispatch(setPayments(response?.data?.split?.splitPayments));
                dispatch(setOrderData(response?.data?.order));
            } else {
                toast.error(response.message);
            }
        })
        .catch((error) => {
            toast.error("Something went wrong, please try again later.");
        });
    }



    // Real-time Pusher listener for order updates (e.g. split terminal payments confirmed by PAX)
    useEffect(() => {
        const handleOrderUpdated = (data) => {
            console.log("Split payment Pusher event [order-updated]:", data);
            if (!data || Number(data.order_id) !== Number(orderData?.id)) return;

            if (
                (data.type === "split_payment_updated" || data.type === "split_payment_paid" || data.type === "terminal_payment_approved") &&
                data.split
            ) {
                toast.success("Split card payment confirmed successfully!");
                setIsCardLoading(false);
                setIsCardResponseOpen(false);
                setCardResponseData(null);
                setCardConfirmPayment(null);
                setIsPaxTimeoutDialogOpen(false);
                setPaxTimeoutData(null);

                dispatch(setSplitData(data.split));

                if (orderData) {
                    const incomingPayment = data.payment || orderData.payment;
                    dispatch(setOrderData({
                        ...orderData,
                        payment_status: data.payment_status || orderData.payment_status,
                        stage: data.stage || orderData.stage,
                        kitchen_status: data.kitchen_status || orderData.kitchen_status,
                        cooking_time: data.cooking_time ?? orderData.cooking_time,
                        cooking_time_set_at: data.cooking_time_set_at ?? orderData.cooking_time_set_at,
                        payment: incomingPayment,
                        tips: incomingPayment?.tips ?? data.tips ?? orderData.tips,
                        tips_amount: incomingPayment?.tips_amount ?? data.tips_amount ?? orderData.tips_amount,
                        tip_type: incomingPayment?.tip_type ?? data.tip_type ?? orderData.tip_type,
                        total_price: data.total_price ?? (incomingPayment?.amount ? incomingPayment.amount : orderData.total_price),
                    }));
                }

                if (data.payment_status === "paid" || Number(data.split?.remaining_amount) <= 0) {
                    setIsCompleted(true);
                }
            } else if (
                data.type === "order_payment_timeout" ||
                data.type === "split_payment_timeout" ||
                data.type === "terminal_payment_timeout"
            ) {
                if (data.split) {
                    dispatch(setSplitData(data.split));
                }
                if (orderData) {
                    dispatch(setOrderData({
                        ...orderData,
                        payment_status: data.payment_status || orderData.payment_status,
                        stage: data.stage || orderData.stage,
                        kitchen_status: data.kitchen_status || orderData.kitchen_status,
                        cooking_time: data.cooking_time ?? orderData.cooking_time,
                        cooking_time_set_at: data.cooking_time_set_at ?? orderData.cooking_time_set_at,
                        payment: data.payment || null,
                    }));
                }
                setIsCardLoading(false);
                setIsCardResponseOpen(false);
                setCardResponseData(null);
                setCardConfirmPayment(null);
                setPaxTimeoutData(data);
                setIsPaxTimeoutDialogOpen(true);
                toast.error("PAX terminal payment timed out.");
            } else if (
                data.type === "split_payment_failed" ||
                data.type === "split_payment_cancelled"
            ) {
                toast.error(data?.message || "Split card payment cancelled.");
                setIsCardLoading(false);
                setIsCardResponseOpen(false);
                setCardResponseData(null);
                setCardConfirmPayment(null);
                setIsPaxTimeoutDialogOpen(false);
                setPaxTimeoutData(null);
            }
        };

        const unsubscribe = addEventHandler("order-updated", handleOrderUpdated);
        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            } else {
                removeEventHandler("order-updated", handleOrderUpdated);
            }
        };
    }, [addEventHandler, removeEventHandler, orderData, dispatch]);

    useEffect(() => {
        if (!restaurant_id || !orderData?.id) return;
        setIsLoading(true);
        getPaymentSplitApi(restaurant_id, orderData.id)
            .then((response) => {
                console.log("get payment split response ->", response);
                if (response.status === "success") {
                    const serverSplit = response?.data?.splits?.[0];
                    if (serverSplit && Number(serverSplit.order_id) === Number(orderData.id)) {
                        dispatch(setSplitData(serverSplit));

                        if (Number(serverSplit.remaining_amount) <= 0) {
                            setIsCompleted(true);
                        }
                    } else {
                        dispatch(resetSplit({
                            totalAmount: orderData.total_price || 0,
                            order_id: orderData.id,
                        }));
                    }
                } else {
                    toast.error(response.message);
                }
            })
            .catch((error) => {
                console.error("get payment split error:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [restaurant_id, orderData?.id, dispatch]);



    return (
        <ErrorBoundary>
            {isLoading ? (
                <div className="  w-[700px] xl:w-[1200px] h-[590px] tab-max:h-[700px] bg-color2 p-6 rounded-md border-2 border-color3 shadow-2xl text-white flex flex-col gap-4">
                    <div className="flex justify-center">
                        <h2 className="text-2xl font-semibold text-center  -mt-4 uppercase">Split Payment {isCompleted && <span className=" text-color7"> (Completed)</span>} </h2>
                    </div>
                    <div className="w-full flex gap-4 mt-6">
                        <div className="w-[50%] p-4 bg-color3">
                            <Skeleton className="h-10 w-40 mb-4" />
                            <div className="flex items-center gap-4">
                                <Skeleton className="h-12 w-20" />
                                <Skeleton className="h-12 w-12" />
                            </div>
                            <div className="mt-6 space-y-4">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex gap-4 items-center">
                                        <Skeleton className="h-8 w-full" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="w-[50%] p-4">
                            <div className="flex gap-4 justify-between mb-4">
                                <Skeleton className="h-8 w-32" />
                                <Skeleton className="h-8 w-32" />
                            </div>
                            <div className="space-y-2">
                                {[...Array(4)].map((_, i) => (
                                    <Skeleton key={i} className="h-10 w-full" />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-4 mt-4">
                        <Skeleton className="h-10 w-32" />
                        <Skeleton className="h-10 w-32" />
                    </div>
                </div>
            ) : (
            <div className=" w-[700px] xl:w-[1200px] h-[590px] tab-max:h-[700px] bg-color2 p-6 rounded-md border-2 border-color3 shadow-2xl text-white flex flex-col gap-4">
                <h2 className="text-2xl font-semibold text-center  -mt-4 uppercase">Split Payment {isCompleted && <span className=" text-color7"> (Completed)</span>} </h2> 

                <div className=" w-full flex items-center justify-center gap-4">
                    <div className=" w-[50%] pt-6 px-4 bg-color3">
                        <div className=" -mt-4">
                            <p className="font-medium text-center uppercase">Number Of Splits </p>
                        </div>
                        <div className="flex flex-col items-center justify-center gap-4 mb-4 mt-4">
                            <span className=" px-4 text-4xl font-bold">{splitCount}</span>
                            <button
                                onClick={() => handleSplitChange(1)}
                                className="px-3 py-1  bg-color12 text-5xl text-white rounded-md hover:bg-gray-600"
                            >
                                <Plus size={28} />
                            </button>
                        </div>

                        <div className=" w-full h-[300px] tab-max:h-[400px] overflow-y-auto p-2 border-2 border-color3 rounded-md">
                            {payments.map((payment, index) => (
                                <div key={index} className=" w-full flex flex-col bg-color2 items-center gap-4 mb-8 p-3 rounded-md relative">
                                    <div
                                        className="absolute top-0 right-0 bg-color1 p-1 rounded-full flex justify-end text-color8 text-xl cursor-pointer"
                                        onClick={() => {

                                            if(isCompleted){
                                                toast.error("Payment is already completed. No further changes allowed.");
                                                return;
                                            }

                                            if (splitCount <= 2) {
                                                toast.error("You cannot have less than 2 splits."); // Show error message if splitCount is 2 or less
                                                return;
                                            }

                                            if (payment.slots.length === 0) {
                                                dispatch(removePayment({ front_id: payment.front_id })); // Use unique ID instead of index
                                            } else {
                                                toast.error("You cannot remove this payment because the slot is not empty.");
                                            }
                                        }}
                                    >
                                        <FaXmark />
                                    </div>
                                    <div className=" w-full p-2 bg-color3 flex gap-4 rounded-md">
                                        <input
                                            type="text"
                                            value={payment.name}
                                            onChange={(e) => {
                                                if(isCompleted){
                                                    toast.error("Payment is already completed. No further changes allowed.");
                                                    return;
                                                }
                                                dispatch(updatePaymentName({ index, name: e.target.value }));
                                            }}
                                            className="w-full px-3 py-2 bg-color2 border border-gray-600 rounded-md outline-none focus:border-gray-400 placeholder-gray-500"
                                        />
                                        <div className="flex items-center w-full xl:w-[220px] bg-color2 border border-gray-600 rounded-md overflow-hidden focus-within:border-gray-400">
                                            <button
                                                type="button"
                                                disabled={isCompleted}
                                                onClick={() => handleAdjustAmount(index, -1)}
                                                className="w-10 h-10 flex items-center justify-center bg-color3 hover:bg-gray-600 text-white transition-colors disabled:opacity-50 active:bg-gray-700 select-none border-r border-gray-600"
                                                title="Decrease amount by £1"
                                            >
                                                <Minus size={16} />
                                            </button>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={payment.amount}
                                                onChange={(e) => {
                                                    if(isCompleted){
                                                        toast.error("Payment is already completed. No further changes allowed.");
                                                        return;
                                                    }
                                                    const newValue = parseFloat(e.target.value);
                                                    const totalSlotAmount = payment.slots.reduce((slotSum, slot) => slotSum + slot.amount, 0);
                                                    const remaining = payment.amount - totalSlotAmount;

                                                    if (newValue >= totalSlotAmount && newValue <= totalSlotAmount + remainingAmount) {
                                                        dispatch(updatePaymentAmount({ index, value: newValue }));
                                                    } else if (newValue < totalSlotAmount) {
                                                        toast.error("Cannot reduce below the total slot amount.");
                                                    } else {
                                                        toast.error("Exceeds the remaining amount.");
                                                    }
                                                }}
                                                className="flex-1 min-w-0 px-2 py-2 bg-transparent text-center text-white outline-none placeholder-gray-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none font-semibold text-base"
                                            />
                                            <button
                                                type="button"
                                                disabled={isCompleted}
                                                onClick={() => handleAdjustAmount(index, 1)}
                                                className="w-10 h-10 flex items-center justify-center bg-color3 hover:bg-gray-600 text-white transition-colors disabled:opacity-50 active:bg-gray-700 select-none border-l border-gray-600"
                                                title="Increase amount by £1"
                                            >
                                                <Plus size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className=" w-full  flex gap-2">
                                        {/* <button
                                            onClick={() => dispatch(updatePaymentMethod({ index, method: "Cash" }))}
                                            className={` xl:hidden grow px-3 py-2 rounded-md font-medium transition-colors bg-color6 text-white  hover:bg-gray-600"}`}
                                        >
                                            Print Slip
                                        </button> */}
                                        <button
                                            onClick={() => {
                                                if (!orderData?.id) {
                                                    toast.error("Order not found.");
                                                    return;
                                                }

                                                printSplitPaymentCopy(orderData.id, payment?.id);
                                            }}
                                            className={` hidden xl:block grow px-3 py-2 rounded-md font-medium transition-colors bg-color6 text-white  hover:bg-gray-600"}`}
                                        >
                                            Print Customer Copy
                                        </button>
                                        <button
                                            onClick={() => {
                                                if(isCompleted){
                                                    toast.error("Payment is already completed. No further changes allowed.");
                                                }else{
                                                    setActivePaymentId(payment.front_id); // Set the active payment ID
                                                    setIsModalOpen(true); // Open the modal
                                                }
                                            }}
                                            className={`grow px-3 py-2 rounded-md font-medium transition-colors  bg-cviolet text-white hover:bg-gray-600`}
                                        >
                                            Cash
                                        </button>
                                        <button
                                            onClick={() => handleOpenCardModal(payment)}
                                            className={` grow px-3 py-2 rounded-md font-medium transition-colors bg-cyan-500 text-white hover:bg-cyan-600`}
                                        >
                                            Card
                                        </button>
                                        <button
                                            // onClick={}
                                            className={`grow px-3 py-2 rounded-md font-medium transition-colors bg-sky-500 text-white hover:opacity-90`}
                                            onClick={() => {
                                                if (isCompleted) {
                                                    toast.error("Payment is already completed. No further changes allowed.");
                                                    return;
                                                }
                                                setTipsFor(payment.front_id);
                                                const nextType = payment.tipType || "fixed";
                                                setTipType(nextType);
                                                const nextValue = nextType === "percentage" ? payment.tipValue : payment.tipAmount;
                                                setTipAmount(nextValue > 0 ? String(nextValue) : "");
                                            }}
                                        >
                                            Tips
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="w-[50%] flex flex-col">
                        <div className="flex items-center justify-between text-center mb-4 border-b-2 border-dashed border-color3 pb-4">
                            <p className="text-lg font-medium">TOTAL TO PAY</p>
                            <p className="text-xl font-bold text-color6">£{totalAmount.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center justify-between text-center mb-4 border-b-2 border-dashed border-color3 pb-4">
                            <p className="text-lg font-medium">Paid</p>
                            <p className="text-xl font-bold text-color7">
                                £{receivedAmount.toFixed(2)}
                            </p>
                        </div>
                        <div className="flex items-center justify-between text-center mb-4 ">
                            <p className="text-lg font-medium">Remaining</p>
                            <p className="text-xl font-bold text-color8">
                                 £{remainingAmount.toFixed(2)}
                            </p>
                        </div>
                        <div className=" w-full h-[300px] tab-max:h-[400px] pr-2 overflow-y-auto flex flex-col gap-4 ">
                            {payments.map((payment, index) => (
                                <div key={payment.front_id} className="border border-gray-600 rounded-md p-2">
                                    <p className=" -mt-2 pb-1 text-lg font-medium text-center border-b border-color3">{payment.name}</p>
                                    <div className=" pt-1 flex gap-4 justify-between items-center">
                                        <p className=" font-semibold ">Amount: <span className=" text-color6">£{payment.amount.toFixed(2)}</span></p>
                                        <p className=" font-semibold ">Tips: <span className=" text-color6">£{(payment.tipAmount || 0).toFixed(2)}</span></p>
                                        <p className=" font-semibold ">
                                            Remaining: <span className=" text-color8">£{(
                                                (payment.amount + (payment.tipAmount || 0)) -
                                                payment.slots.reduce((slotSum, slot) => slotSum + slot.amount, 0)
                                            ).toFixed(2)}</span>
                                        </p>
                                    </div>
                                    {
                                        payment.slots.length === 0 ? (
                                            <p className="text-center text-sm text-color8 italic mt-2">No slots added yet.</p>
                                        ) : (
                                            <p className="-mt-2 text-center font-semibold text-color6 border-b border-color3">Slots:</p>
                                        )
                                    }
                                    <div className="">
                                        {payment.slots.map((slot, slotIndex) => {
                                            const rec = Number(slot.received ?? slot.amount ?? 0);
                                            const amt = Number(slot.amount ?? 0);
                                            const chg = Number(slot.change ?? (rec - amt) ?? 0);

                                            return (
                                                <div key={slot.id || slotIndex} className="text-sm flex justify-between">
                                                    <div>
                                                        <b>Slot:</b>
                                                        <span> {slotIndex + 1}</span>
                                                    </div>   
                                                    <div>
                                                        <b>Method:</b>
                                                        <span className="capitalize"> {slot.method || "N/A"}</span>
                                                    </div>
                                                    <div>
                                                        <b>Received:</b>
                                                        <span className=" text-color6"> £{rec.toFixed(2)}</span>
                                                    </div>
                                                    <div>
                                                        <b>Amount:</b>
                                                        <span className=" text-color7"> £{amt.toFixed(2)}</span>
                                                    </div>
                                                    <div>
                                                        <b>Change:</b>
                                                        <span className=" text-color8"> £{chg.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex gap-4 -mt-2 tab-max:mt-2">
                    <button
                        onClick={() => setSplitPaymentPage(false)}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-md font-medium transition-colors"
                    >
                        Close
                    </button>
                    <button
                        onClick={updatePaymentSplit}
                        className={`flex-1  bg-color7 hover:bg-green-600 text-white py-2 rounded-md font-medium transition-colors ${isCompleted ? "bg-green-600 cursor-not-allowed" : ""}`}
                    >
                        Save
                    </button>
                </div>
                {isModalOpen && (
                    <DefaultDialog title="Cash Payment" hideButtons={true} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                        <div className="p-4">
                            <div className=" flex gap-4">
                                <div>
                                    <h2 className=" font-bold mb-2">Received</h2>
                                    <input
                                        type="number"
                                        value={cashReceived}
                                        onChange={(e) =>{ setCashReceived(parseFloat(e.target.value) || 0); setCashAmount(parseFloat(e.target.value) || 0); 20}}
                                        className="w-full px-3 py-2 bg-color3 border rounded-md outline-none focus:border-blue-500"
                                        placeholder="Enter amount"
                                    />
                                </div>
                                <div>
                                    <h2 className=" font-bold mb-2">Amount</h2>
                                    <input
                                        type="number"
                                        value={cashAmount}
                                        onChange={(e) => setCashAmount(parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 bg-color3 border rounded-md outline-none focus:border-blue-500"
                                        placeholder="Enter amount"
                                    />
                                </div>
                                <div>
                                    <h2 className=" font-bold mb-2">Change</h2>
                                    <div className="w-[100px] py-2 flex items-center justify-center bg-color3 border rounded-md "><span>{Number(cashReceived - cashAmount).toFixed(2)}</span></div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-4 mt-4">
                                <button
                                    onClick={() =>{ setIsModalOpen(false); setCashReceived(0); setCashAmount(0); }}
                                    className=" w-full px-4 py-2 bg-color14 rounded-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (cashReceived > 0) {
                                            dispatch(
                                                addPaymentSlot({
                                                    front_id: activePaymentId,
                                                    slot: { method: "Cash", received: cashReceived, amount: cashAmount, changed: cashReceived - cashAmount },
                                                })
                                            );
                                            setIsModalOpen(false);
                                            setCashReceived(0);
          
                                            // updatePaymentSplit();

                                            setCashReceived(0);
                                            setCashAmount(0);

                                            
                                        } else {
                                            toast.error("Please enter a valid amount.");
                                        }
                                    }}
                                    className={" w-full px-4 py-2 bg-color7 text-white rounded-md "}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </DefaultDialog>
                )}
                {tipsFor && (
                    <DefaultDialog title="Tips / Gratuity" hideButtons={true} isOpen={true} onClose={() => setTipsFor(null)}>
                        <TipsCourseWiseAndSplit
                            setTipsFor={setTipsFor}
                            tipType={tipType}
                            setTipType={setTipType}
                            amount={tipAmount}
                            setAmount={setTipAmount}
                            saveTipsInfo={saveTipsInfo}
                        />
                    </DefaultDialog>
                )}

                <SplitCardPaymentConfirmDialog
                    isOpen={!!cardConfirmPayment}
                    onClose={() => setCardConfirmPayment(null)}
                    onConfirm={handleDispatchCardPayment}
                    isLoading={isCardLoading}
                    payment={cardConfirmPayment}
                    maxAmount={cardChargeAmount}
                    orderData={orderData}
                />

                <OrderCardPaymentResponseDialog
                    isOpen={isCardResponseOpen}
                    onClose={() => setIsCardResponseOpen(false)}
                    responseData={cardResponseData}
                    orderData={orderData}
                    onPaymentCompleted={(res) => {
                        if (res?.data?.order) {
                            dispatch(setOrderData(res.data.order));
                        }
                    }}
                    onPaymentCancelled={(res) => {
                        if (res?.data?.order) {
                            dispatch(setOrderData(res.data.order));
                        }
                    }}
                />

                <PaxPaymentTimeoutDialog
                    isOpen={isPaxTimeoutDialogOpen}
                    onClose={() => setIsPaxTimeoutDialogOpen(false)}
                    onNewPayment={() => {
                        setIsPaxTimeoutDialogOpen(false);
                        toast("Please select a split payment slot and click Card to dispatch new payment.");
                    }}
                    orderData={orderData}
                    timeoutData={paxTimeoutData}
                    isLoading={isCardLoading}
                />
            </div>
            )}
        </ErrorBoundary>
    );
}

export default SplitPayment;